import { PaziaAsyncDropdown } from "components/atoms";
import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import * as yup from "yup";
import { CourseSessionOption } from "api/hooks";
import { PaziaSelect, IDropdownOption } from "components/atoms";
import { fromTimeFormat } from "utils";
import {
  formatDate,
  minutesToTime,
  timeToMinutes,
  toTimeFormat,
} from "utils";
import { PaziaTimeInput } from "components/atoms/Inputs";

interface ScheduleFormProps {
  sessionsOptions: CourseSessionOption[];
  children?: React.ReactNode;
}

export const BaseScheduleForm = ({ sessionsOptions, children }: ScheduleFormProps): JSX.Element => {
  const { watch, setValue } = useFormContext();
  const [fromTimeOptions] = useState(timeOptions);
  const [toTimeOptions, setToTimeOptions] = useState(timeOptions);

  const session = watch(BASE_SCHEDULE_FORM_NAMES.SESSION);
  const time = watch(BASE_SCHEDULE_FORM_NAMES.TIME);
  const fromTime = watch(BASE_SCHEDULE_FORM_NAMES.FROM_TIME);

  const onTimeChange = (t: React.ChangeEvent<HTMLInputElement>) => {
    const durration = isNaN(Number(t.target.value)) ? timeToMinutes(t.target.value) : Number(t.target.value);
    const newToTime = fromTime + durration; //* { fromTime: 300, durration: 100 }
    let newToTimeOpt = timeOptions.find((x) => x.value === newToTime);
    if (!newToTimeOpt) {
      newToTimeOpt = { label: toTimeFormat(newToTime), value: newToTime };
      setToTimeOptions([...timeOptions, newToTimeOpt].sort((a, b) => a.value - b.value));
    }
    if (newToTimeOpt.value > 1320) {
      newToTimeOpt = timeOptions.find((x) => x.value === 1320)!;
    }
    setValue(BASE_SCHEDULE_FORM_NAMES.TO_TIME, newToTimeOpt.value);
  };

  const onFromChange = (ft: IDropdownOption) => {
    const newToTime = +ft.value + timeToMinutes(time);
    let newToTimeOpt = timeOptions.find((x) => x.value === newToTime);
    if (!newToTimeOpt) {
      newToTimeOpt = { label: toTimeFormat(newToTime), value: newToTime };
      setToTimeOptions([...timeOptions, newToTimeOpt].sort((a, b) => a.value - b.value));
    }
    if (newToTimeOpt.value > 1320) {
      newToTimeOpt = timeOptions.find((x) => x.value === 1320)!;
    }
    setValue(BASE_SCHEDULE_FORM_NAMES.TO_TIME, newToTimeOpt.value);
  };

  const onToTimeChange = (tt: IDropdownOption) => {
    const durration = +tt.value - Number(fromTime);
    setValue(BASE_SCHEDULE_FORM_NAMES.TIME, minutesToTime(durration));
  };

  const getOptionsFromDateToDate = (fromdate: Date, to: Date): IDropdownOption[] => {
    const options: IDropdownOption[] = [];
    const from = new Date(fromdate);

    // set from data time to 2:00
    from.setHours(2, 0, 0, 0);
    // set to data time to 22:00
    to.setHours(22, 0, 0, 0);

    while (from <= to) {
      options.push({ label: formatDate(from), value: formatDate(from) });
      from.setDate(from.getDate() + 1);
    }

    return options;
  };

  const loadDays = async () => {
    const selectedSession = sessionsOptions.find((x) => x.value == session)?.session;
    const dayOptions = selectedSession
      ? getOptionsFromDateToDate(selectedSession.dateFrom, selectedSession.dateTo)
      : [];
    return dayOptions;
  };

  return (
    <>
      {/* Phones: one field per row; the two/three-up rows squeezed every select to "Wyb…" */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <PaziaSelect
          className="sm:w-1/2"
          name={BASE_SCHEDULE_FORM_NAMES.SESSION}
          options={sessionsOptions}
          isClearable={false}
          required
          label="Zjazd"
        />
        <PaziaAsyncDropdown
          disabled={!session}
          required
          isClearable={false}
          className="sm:w-1/2"
          name={BASE_SCHEDULE_FORM_NAMES.DAY}
          loadOptions={loadDays}
          loadOnRender
          label="Dzień"
        />
      </div>
      {children}
      <div className="col-span-12 flex flex-col gap-2 sm:flex-row sm:justify-between">
        <PaziaSelect
          label="Od"
          required
          name={BASE_SCHEDULE_FORM_NAMES.FROM_TIME}
          options={fromTimeOptions}
          onChange={onFromChange}
          isClearable={false}
          className="sm:w-1/3"
        />
        <div className="sm:mb-2 sm:mt-auto sm:w-28">
          <PaziaTimeInput name={BASE_SCHEDULE_FORM_NAMES.TIME} onBlur={onTimeChange} label="Czas trwania" required />
        </div>
        <PaziaSelect
          label="Do"
          required
          name={BASE_SCHEDULE_FORM_NAMES.TO_TIME}
          options={toTimeOptions.filter((x) => x.value > fromTime)}
          onChange={onToTimeChange}
          isClearable={false}
          className="sm:w-1/3"
        />
      </div>
    </>
  );
};

const BASE_SCHEDULE_FORM_NAMES = {
  SESSION: "session",
  DAY: "day",
  TIME: "time",
  FROM_TIME: "fromTime",
  TO_TIME: "toTime",
} as const;

const baseScheduleSchema = yup
  .object({
    [BASE_SCHEDULE_FORM_NAMES.SESSION]: yup.string().required(),
    [BASE_SCHEDULE_FORM_NAMES.DAY]: yup.string().required(),
    [BASE_SCHEDULE_FORM_NAMES.FROM_TIME]: yup.number().required(),
    [BASE_SCHEDULE_FORM_NAMES.TO_TIME]: yup.number().required(),
    [BASE_SCHEDULE_FORM_NAMES.TIME]: yup.string().required(),
  })
  .required();

export type BaseScheduleFormSchema = yup.InferType<typeof baseScheduleSchema>;

const timeOptions = Array.from({ length: 69 }).map((_, i) => ({
  label: toTimeFormat(300 + i * 15),
  value: 300 + i * 15,
}));

const resolveDefaultValues = (model?: {
  session: { id: number };
  dateFrom: Date;
  timeFrom: string;
  timeTo: string;
}) => {
  const defaultValues = {
    [BASE_SCHEDULE_FORM_NAMES.SESSION]: model?.session?.id?.toString() ?? undefined,
    [BASE_SCHEDULE_FORM_NAMES.DAY]: model?.dateFrom ? formatDate(model.dateFrom) : "",
    [BASE_SCHEDULE_FORM_NAMES.FROM_TIME]: model?.timeFrom ? fromTimeFormat(model.timeFrom) : undefined,
    [BASE_SCHEDULE_FORM_NAMES.TO_TIME]: model?.timeTo ? fromTimeFormat(model.timeTo) : undefined,
    [BASE_SCHEDULE_FORM_NAMES.TIME]:
      model?.timeTo && model?.timeFrom
        ? minutesToTime(fromTimeFormat(model.timeTo) - fromTimeFormat(model.timeFrom))
        : undefined,
  };
  return defaultValues;
};

export const ScheduleFormBase = {
  schema: baseScheduleSchema,
  resolveDefaultValues,
  fields: BASE_SCHEDULE_FORM_NAMES,
};
